import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  selectPrefix,
  selectWidth,
  selectreportFromDate,
  selectreportToDate,
  setreportsFromDate,
  setreportsTodate,
} from "../../utility/Redux/profile";
import { selectToken } from "../../utility/Redux/security";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { GetItemWiseSaleReport, GetSubSectionWiseSaleReport } from "./func";
import {
  ItemWiseSaleReportColumns,
  ItemWiseSaleReportPrintColumns,
  SubSectionWiseSaleReportColumns,
} from "./columns";
import { SectionWiseSaleReportColumns } from "./columns";
import { GetSectionWiseSaleReport } from "./func";
import Footer from "./footer";
import GeniusTableReports from "../../utility/component/geniusComponents/geniusTableData";
import CustomerMasterHelp from "../../help/customermasterHelp";
import IsLocalData from "../../utility/component/isLocalData";
import FromToDateMobile from "../../utility/component/fromToDateMobile";
import { SectionWiseSale } from "../../billing/comp";
import { FromToDate } from "../../utility/component/fromToDate";
import { PrintButton } from "../../utility/component/buttons";
import GeniusTableForReports from "./printReport";
import { FormatNumber2 } from "../../utility/functions/formatNumber";
import NewPrintReport from "../../utility/component/datagrid/newPrintReport";
import { Pagination } from "@mui/material";

const toNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (!value) {
    return 0;
  }

  const amount = Number(String(value).replace(/,/g, "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const compactNumber = (value) => FormatNumber2(toNumber(value));

const buildSaleInsights = (rows = [], summaryRows = []) => {
  const byItem = {};
  const byCustomer = {};
  const bySection = {};
  const billSet = new Set();
  const hasSummaryRows = Boolean(summaryRows[0]);

  rows.forEach((row) => {
    const itemName = row.ITEMNAME || "Unknown Item";
    const customerName = row.CUSTOMER_NAME || row.CUSTOMER_MOBILE || "Walk-in";
    const sectionName = row.SECTION_NAME || row.SEC_NAME || "Unassigned";
    const quantity = toNumber(row.QNTY);
    const amount = toNumber(row.GTOTAL || row.AMOUNT);

    if (!byItem[itemName]) {
      byItem[itemName] = {
        label: itemName,
        quantity: 0,
        amount: 0,
        filterType: "item",
      };
    }
    byItem[itemName].quantity += quantity;
    byItem[itemName].amount += amount;

    if (!byCustomer[customerName]) {
      byCustomer[customerName] = {
        label: customerName,
        quantity: 0,
        amount: 0,
        filterType: "customer",
      };
    }
    byCustomer[customerName].quantity += quantity;
    byCustomer[customerName].amount += amount;

    if (!hasSummaryRows && !bySection[sectionName]) {
      bySection[sectionName] = {
        label: sectionName,
        quantity: 0,
        amount: 0,
        filterType: "section",
      };
    }
    if (!hasSummaryRows) {
      bySection[sectionName].quantity += quantity;
      bySection[sectionName].amount += amount;
    }

    if (row.BILL_NO || row.BILLNO || row.INVOICE_NO) {
      billSet.add(row.BILL_NO || row.BILLNO || row.INVOICE_NO);
    }
  });

  summaryRows.forEach((row) => {
    const label = row.SECTION_NAME || row.SUB_SEC_NAME;
    if (!label) {
      return;
    }

    if (!bySection[label]) {
      bySection[label] = {
        label,
        quantity: 0,
        amount: 0,
        filterType: row.SUB_SEC_NAME ? "subSection" : "section",
      };
    }
    bySection[label].quantity += toNumber(row.QNTY);
    bySection[label].amount += toNumber(row.GTOTAL || row.AMOUNT);
  });

  const topItems = Object.values(byItem)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 10);
  const topCustomers = Object.values(byCustomer)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 8);
  const topSections = Object.values(bySection)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 8);
  const totalAmount = hasSummaryRows
    ? summaryRows.reduce((acc, row) => acc + toNumber(row.GTOTAL || row.AMOUNT), 0)
    : rows.reduce((acc, row) => acc + toNumber(row.GTOTAL || row.AMOUNT), 0);
  const totalQuantity = hasSummaryRows
    ? summaryRows.reduce((acc, row) => acc + toNumber(row.QNTY), 0)
    : rows.reduce((acc, row) => acc + toNumber(row.QNTY), 0);

  return {
    totalAmount,
    totalQuantity,
    rowCount: rows.length,
    billCount: billSet.size,
    topItem: topItems[0],
    topItems,
    topCustomers,
    topSections,
  };
};

const SaleMetricCard = ({ label, value, subText, tone = "default" }) => {
  const toneClass =
    tone === "sale"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "qty"
      ? "border-blue-200 bg-blue-50 text-blue-900"
      : tone === "focus"
      ? "border-orange-200 bg-orange-50 text-orange-900"
      : "border-slate-200 bg-white text-slate-800";

  return (
    <div className={`rounded-md border px-3 py-2 shadow-sm ${toneClass}`}>
      <div className="text-[11px] font-semibold uppercase opacity-70">
        {label}
      </div>
      <div className="mt-1 truncate text-lg font-bold">{value}</div>
      {subText ? <div className="truncate text-xs opacity-70">{subText}</div> : null}
    </div>
  );
};

const SaleBarRow = ({
  label,
  value,
  maxValue,
  subText,
  tone = "sale",
  onClick,
  rightText,
}) => {
  const percent = maxValue > 0 ? Math.max((value / maxValue) * 100, 3) : 0;
  const barClass = tone === "qty" ? "bg-blue-500" : "bg-emerald-500";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full space-y-1 rounded-md text-left ${
        onClick ? "cursor-pointer hover:bg-slate-50" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="truncate font-semibold text-slate-700">{label}</span>
        <span className="shrink-0 font-bold text-slate-900">
          {rightText || compactNumber(value)}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100">
        <div
          className={`h-2 rounded-full ${barClass}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {subText ? <div className="text-[11px] text-slate-500">{subText}</div> : null}
    </button>
  );
};

const ItemWiseSaleSummary = ({ insights }) => {
  return (
    <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
      <SaleMetricCard
        label="Total Sale"
        value={compactNumber(insights.totalAmount)}
        subText={`${insights.rowCount} item rows`}
        tone="sale"
      />
      <SaleMetricCard
        label="Total Qty"
        value={compactNumber(insights.totalQuantity)}
        subText="Sold quantity"
        tone="qty"
      />
      {/* <SaleMetricCard
        label="Bills"
        value={insights.billCount || "-"}
        subText={insights.billCount ? "Unique bills" : "Bill no not available"}
      /> */}
      <SaleMetricCard
        label="Top Item"
        value={insights.topItem ? insights.topItem.label : "-"}
        subText={insights.topItem ? compactNumber(insights.topItem.amount) : "No sale yet"}
        tone="focus"
      />
    </div>
  );
};

const GraphBlock = ({
  title,
  rows,
  valueKey = "amount",
  tone = "sale",
  onRowClick,
}) => {
  console.log(rows)
  const maxValue = Math.max(...rows.map((row) => toNumber(row[valueKey])), 1);

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-3 text-xs font-bold uppercase text-slate-500">
        {title}
      </div>
      <div className="space-y-3">
        {rows.length ? (
          rows.map((row) => (
            <SaleBarRow
              key={`${title}-${row.label}`}
              label={row.label}
              value={toNumber(row[valueKey])}
              maxValue={maxValue}
              rightText={
                valueKey === "quantity"
                  ? `${compactNumber(row.quantity)} qty | ${compactNumber(row.amount)}`
                  : undefined
              }
              subText={
                row.quantity
                  ? `Qty ${compactNumber(row.quantity)}`
                  : undefined
              }
              tone={tone}
              onClick={onRowClick ? () => onRowClick(row, title) : undefined}
            />
          ))
        ) : (
          <div className="rounded-md bg-slate-50 p-4 text-center text-sm text-slate-500">
            No data for graph
          </div>
        )}
      </div>
    </div>
  );
};

const ItemWiseSaleGraph = ({ insights, onGraphRowClick }) => {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 xl:grid-cols-3">
        <GraphBlock
          title="Top 10 Items By Sale"
          rows={insights.topItems}
          onRowClick={onGraphRowClick}
        />
        <GraphBlock
          title="Top Items By Qty"
          rows={[...insights.topItems].sort((a, b) => b.quantity - a.quantity)}
          valueKey="quantity"
          tone="qty"
          onRowClick={onGraphRowClick}
        />
        <GraphBlock
          title="Top Customers"
          rows={insights.topCustomers}
          onRowClick={onGraphRowClick}
        />
      </div>
      <GraphBlock
        title="Section / Sub Section Sale"
        rows={insights.topSections}
        onRowClick={onGraphRowClick}
      />
    </div>
  );
};

const matchesInsightRow = (record, selectedRow) => {
  if (!selectedRow) {
    return false;
  }

  const label = selectedRow.label;

  if (selectedRow.filterType === "item") {
    return (record.ITEMNAME || "Unknown Item") === label;
  }

  if (selectedRow.filterType === "customer") {
    return (
      (record.CUSTOMER_NAME || record.CUSTOMER_MOBILE || "Walk-in") === label
    );
  }

  return (
    record.SECTION_NAME === label ||
    record.SEC_NAME === label ||
    record.SUB_SEC_NAME === label
  );
};

const DrilldownModal = ({ open, title, rows, columns, onClose }) => {
  if (!open) {
    return null;
  }

  const totalAmount = rows.reduce(
    (acc, row) => acc + toNumber(row.GTOTAL || row.AMOUNT),
    0
  );
  const totalQuantity = rows.reduce((acc, row) => acc + toNumber(row.QNTY), 0);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[86vh] w-[94vw] flex-col rounded-md bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-base font-bold text-slate-900">
              {title}
            </div>
            <div className="text-xs text-slate-500">
              {rows.length} records | Qty {compactNumber(totalQuantity)} | Sale{" "}
              {compactNumber(totalAmount)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 p-3">
          {rows.length ? (
            <GeniusTableReports
              columns={columns}
              data={rows}
              footer={<Footer columns={columns} rows={rows} />}
            />
          ) : (
            <div className="flex h-full items-center justify-center rounded-md border text-sm text-slate-500">
              No related records found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const IemWiseSaleReport = () => {
  const location = useLocation();
  const { FDATE, TDATE, SECTION_NAME } = location.state || {}; // Access state
  const prefix = useSelector(selectPrefix);
  const [purchaseList, setPurchaseList] = useState([]);
  const fromDate = useSelector(selectreportFromDate);
  const ToDate = useSelector(selectreportToDate);
  const width = useSelector(selectWidth);
  const [secName, setSecName] = useState("");
  const [subSecName, setSubSecName] = useState("");
  const [cusMobile, setCusMobile] = useState("");
  const [fullItemWiseReport, setFullItemWiseReport] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [sectionWiseSaleStatus, setSectionWiseSaleStatus] = useState(false);
  const [selectedType, setSelectedType] = useState("section");
  const [activeView, setActiveView] = useState("report");
  const [drilldown, setDrilldown] = useState(null);
 
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
 const [getSectionData, setSectionData] = useState({
    table_prefix: prefix,
    FDATE: moment(fromDate).format("YYYY-MM-DD"),
    TDATE: moment(ToDate).format("YYYY-MM-DD"),
    CUSTOMER_MOBILE: cusMobile,
    SECTION_NAME: secName,
    SUB_SEC_NAME: subSecName,
  });

  const columns = ItemWiseSaleReportColumns(width);
  const cols = SectionWiseSaleReportColumns(width);
  const cols_sub = SubSectionWiseSaleReportColumns(width);
  const [customerHelpStatus, setCustomerHelpStatus] = useState(false);
  const [summaryList, setSummaryList] = useState([]);

  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const insightRows = fullItemWiseReport[0] ? fullItemWiseReport : purchaseList;
  const saleInsights = React.useMemo(
    () => buildSaleInsights(insightRows, summaryList),
    [insightRows, summaryList]
  );
  const drilldownRows = React.useMemo(() => {
    if (!drilldown) {
      return [];
    }

    return insightRows.filter((row) => matchesInsightRow(row, drilldown.row));
  }, [drilldown, insightRows]);
  const openGraphDrilldown = (row, sourceTitle) => {
    setDrilldown({
      row,
      title: `${sourceTitle} - ${row.label}`,
    });
  };
  const okButton = (page) => {
    const additionalData = {
      table_prefix: prefix,
      FDATE: moment(fromDate).format("YYYY-MM-DD"),
      TDATE: moment(ToDate).format("YYYY-MM-DD"),
      SECTION_NAME: secName,
      CUSTOMER_MOBILE: cusMobile,
      SUB_SEC_NAME: subSecName,
      PAGE:page?page:1
    };
    if (selectedType === "section") {
      GetSectionWiseSaleReport(getSectionData, token, dispatch, setSummaryList);
    } else {
      GetSubSectionWiseSaleReport(
        getSectionData,
        token,
        dispatch,
        setSummaryList,   
      );
    }
    GetItemWiseSaleReport(
      additionalData,
      token,
      dispatch,
      setPurchaseList,
      setFullItemWiseReport,  setTotalPages,
      setCurrentPage,
    );
    setSummaryList([]);
    setPurchaseList([]);
  };

  useEffect(() => {
    if ((FDATE, TDATE, SECTION_NAME)) {
      const additionalData = {
        table_prefix: prefix,
        FDATE: moment(FDATE).format("YYYY-MM-DD"),
        TDATE: moment(TDATE).format("YYYY-MM-DD"),
        SECTION_NAME: SECTION_NAME,
        CUSTOMER_MOBILE: "",
        SUB_SEC_NAME: subSecName,
      };

      GetSectionWiseSaleReport(
        {
          table_prefix: prefix,
          FDATE: moment(FDATE).format("YYYY-MM-DD"),
          TDATE: moment(TDATE).format("YYYY-MM-DD"),
          CUSTOMER_MOBILE: "",
          SECTION_NAME: SECTION_NAME,
          SUB_SEC_NAME: subSecName,
        },
        token,
        dispatch,
        setSummaryList
      );
      GetItemWiseSaleReport(
        additionalData,
        token,
        dispatch,
        setPurchaseList,
        setFullItemWiseReport
      );
      setSummaryList([]);
      setPurchaseList([]);
    }
  }, [FDATE, TDATE, SECTION_NAME]);
  const printColumns = ItemWiseSaleReportPrintColumns(width);
  const sectionPrintRef = useRef(null);
  useEffect(() => {
    setSectionData((e) => {
      return {
        ...e,
        FDATE: moment(fromDate).format("YYYY-MM-DD"),
        TDATE: moment(ToDate).format("YYYY-MM-DD"),
        CUSTOMER_MOBILE: cusMobile,
        SECTION_NAME: secName,
        SUB_SEC_NAME: subSecName,
      };
    });
  }, [fromDate, ToDate, secName, cusMobile, subSecName]);

  const navigate = useNavigate();

  const printRef = useRef();
  const thermalPrintRef = useRef();
  const [selectedPrintOption, setSelectedPrintOption] = useState("print");

  const handlePrint = useReactToPrint({
    content: () =>
      selectedPrintOption === "print"
        ? printRef.current
        : thermalPrintRef.current,

    onAfterPrint: () => {
      navigate("/item_wise_sale_report");
      setSelectedPrintOption("print");
    },
  });

  const handlePrintSection = useReactToPrint({
    content: () => sectionPrintRef.current,

    pageStyle: `
        @page {
          size: portrait;
        }
      `,
    onAfterPrint: () => {
      navigate("/item_wise_sale_report");
    },
  });

  useEffect(() => {
    const callback = (details, data) => {
      setSectionData(details[0]);
      setSummaryList(data);
      dispatch(setreportsFromDate(details[0].FDATE));
      dispatch(setreportsTodate(details[0].TDATE));
    };
    const elseFunc = () => {
      console.log("nothing");
    };
    IsLocalData("sectionWiseSale", callback, elseFunc);
  }, []);

  useEffect(() => {
    const callback = (details, data) => {
      setFullItemWiseReport(data);
      setPurchaseList(data);
      dispatch(setreportsFromDate(details[0].FDATE));
      dispatch(setreportsTodate(details[0].TDATE));
    };
    const elseFunc = () => {
      console.log("nothing");
    };
    IsLocalData("itemWiseSale", callback, elseFunc);
  }, []);
  //test
  return (
    <div className=" bg-white flex flex-col h-full  overflow-y-scroll  ">
      <div className="flex w-full h-full">
        {width > 1000 ? (
          <div
            className="flex flex-col  pl-3 py-3 pr-0"
            style={{
              width: cols.reduce((acc, row) => acc + Number(row.width), 0) + 35,
              height: "100%",
            }}
          >
            <div className="text-sm mb-2 w-full">
              <div className="grid grid-cols-2 gap-2 w-full">
                <div className="bg-[#f09536] p-2 rounded-sm shadow-md flex justify-center">
                  <div className="flex">
                    <input
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedType("section");
                          GetSectionWiseSaleReport(
                            getSectionData,
                            token,
                            dispatch,
                            setSummaryList
                          );
                        }
                      }}
                      type="radio"
                      checked={selectedType === "section"}
                    />
                    <div className="ml-2">Section</div>
                  </div>
                </div>

                <div className="bg-[#ef750f] p-2 rounded-sm shadow-md flex justify-center">
                  <div className="flex">
                    <input
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedType("sub-section");
                          GetSubSectionWiseSaleReport(
                            getSectionData,
                            token,
                            dispatch,
                            setSummaryList
                          );
                        }
                      }}
                      type="radio"
                      checked={selectedType === "sub-section"}
                    />
                    <div className="ml-2">Sub Section</div>
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                height: `calc(100% - 80px)`,
                width:
                  cols.reduce((acc, row) => acc + Number(row.width), 0) + 10,
              }}
            >
              <GeniusTableReports
                onRowClick={(e) => {
                  const data = {
                    table_prefix: prefix,
                    FDATE: moment(fromDate).format("YYYY-MM-DD"),
                    TDATE: moment(ToDate).format("YYYY-MM-DD"),
                    CUSTOMER_MOBILE: cusMobile,
                    SECTION_NAME:
                      selectedType === "section" ? e.values.SECTION_NAME : "",
                    SUB_SEC_NAME:
                      selectedType === "sub-section"
                        ? e.values.SUB_SEC_NAME
                        : "",
                  };
                  setSecName(e.values.SECTION_NAME);

                  GetItemWiseSaleReport(
                    data,
                    token,
                    dispatch,
                    setPurchaseList,
                    setFullItemWiseReport
                  );
                }}
                columns={selectedType === "section" ? cols : cols_sub}
                data={summaryList}
                footer={<Footer columns={cols} rows={summaryList} />}
              />
            </div>
            <div
              style={{
                display: "none",
              }}
            >
              <div ref={sectionPrintRef}>
                <NewPrintReport
                  details={[
                    {
                      name: "From Date",
                      value: moment(fromDate).format("DD/MM/YYYY"),
                    },
                    {
                      name: "To Date",
                      value: moment(ToDate).format("DD/MM/YYYY"),
                    },
                  ]}
                  title={"Item Wise Sale"}
                  data={summaryList}
                  columns={cols}
                  footer={
                    <Footer
                      isFeilds={
                        <div className="flex justify-end text-sm mt-2">
                          <div>
                            <div className="flex ">
                              <div className="font-semibold w-[160px] text-right ">
                                Grand Total :
                              </div>
                              <div className="ml-2 flex flex-1 justify-end ">
                                {FormatNumber2(
                                  summaryList.reduce(
                                    (acc, row) => acc + Number(row.GTOTAL),
                                    0
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                      columns={cols}
                      rows={summaryList}
                    />
                  }
                />
              </div>
            </div>
            <PrintButton onClick={handlePrintSection} />
          </div>
        ) : null}
        <div
          style={{
            width: `calc(100% -  ${
              width > 1000
                ? cols.reduce((acc, row) => acc + Number(row.width), 0)
                : 0
            }px)`,
          }}
          className="flex "
        >
          <div className=" flex flex-col w-full   p-3 ">
            <div className="w-full">
              {width > 1281 ? (
                <FromToDate
                  onPrintClick={handlePrint}
                  onCustomerSearchClick={() => {
                    setCustomerHelpStatus(true);
                  }}
                  customerClear={() => {
                    setCustomerName("");
                    setCusMobile("");
                  }}
                  hideReset={true}
                  customerData={{
                    CUSTOMER_MOBILE: cusMobile,
                    CUSTOMER_NAME: customerName,
                  }}
                  submit={()=>{
                    okButton()
                  }}
                  reset={() => {
                    setSecName("");
                    setCusMobile("");
                    setPurchaseList([]);
                    setSummaryList([]);
                    setCustomerName("");
                  }}
                />
              ) : (
                <FromToDateMobile
                  customerClear={() => {
                    setCustomerName("");
                    setCusMobile("");
                  }}
                  onPrintClick={handlePrint}
                  sectionHelp={
                    <SectionWiseSale
                      normaltext={true}
                      section={secName}
                      setIsSectionDraweropen={setSectionWiseSaleStatus}
                      isSectionDrawerOpen={sectionWiseSaleStatus}
                      data={
                        <GeniusTableReports
                          onRowClick={(e) => {
                            setSectionWiseSaleStatus(false);

                            const data = {
                              table_prefix: prefix,
                              FDATE: moment(fromDate).format("YYYY-MM-DD"),
                              TDATE: moment(ToDate).format("YYYY-MM-DD"),
                              CUSTOMER_MOBILE: getSectionData.CUSTOMER_MOBILE,
                              SECTION_NAME: e.values.SECTION_NAME,
                              SUB_SEC_NAME: "",
                            };
                            setSecName(e.values.SECTION_NAME);

                            GetItemWiseSaleReport(
                              data,
                              token,
                              dispatch,
                              setPurchaseList,
                              setFullItemWiseReport
                            );
                          }}
                          columns={cols}
                          data={summaryList}
                          footer={() => console.log("hi")}
                        />
                      }
                    />
                  }
                  onCustomerSearchClick={() => {
                    setCustomerHelpStatus(true);
                  }}
                  customerData={{
                    CUSTOMER_MOBILE: cusMobile,
                    CUSTOMER_NAME: customerName,
                  }}
                  submit={()=>{
                    okButton()
                  }}
                />
              )}
            </div>
            <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-800">
                    Item Wise Sale
                  </div>
                  <div className="truncate text-xs text-slate-500">
                    {secName ? `Filtered by ${secName}` : "All sections"}
                    {customerName ? ` | ${customerName}` : ""}
                  </div>
                </div>
                <div className="flex overflow-hidden rounded-md border border-slate-300 bg-white">
                  <button
                    type="button"
                    onClick={() => setActiveView("report")}
                    className={`px-4 py-1.5 text-sm font-semibold ${
                      activeView === "report"
                        ? "bg-slate-900 text-white"
                        : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    Report
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveView("graph")}
                    className={`border-l border-slate-300 px-4 py-1.5 text-sm font-semibold ${
                      activeView === "graph"
                        ? "bg-slate-900 text-white"
                        : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    Graph
                  </button>
                </div>
              </div>
              <div className="mt-2">
                <ItemWiseSaleSummary insights={saleInsights} />
              </div>
            </div>

            <div
              style={{
                height: `calc(100% - ${
                  width < 1000 ? 410 : width < 1300 ? 260 : 185
                }px)`,
                width: "100%",
              }}
              className="mt-2 overflow-auto"
            >
              {activeView === "graph" ? (
                insightRows[0] || summaryList[0] ? (
                  <ItemWiseSaleGraph
                    insights={saleInsights}
                    onGraphRowClick={openGraphDrilldown}
                  />
                ) : (
                  <div className="border rounded-sm h-full flex text-sm text-gray-500 items-center justify-center">
                    No Records Found
                  </div>
                )
              ) : purchaseList[0] ? (
                <GeniusTableReports
                  columns={columns}
                  data={purchaseList}
                  pagination={  <div
                    style={{
                      width: columns.reduce(
                        (acc, row) => acc + Number(row.width),
                        0
                      ),
                    }}
                    className="flex justify-center mt-1"
                  >
                    {" "}
                    <Pagination
                     sx={{
                      "& .MuiPaginationItem-root": {
                        color: "#000",
                        backgroundColor: "#f3f4f6", // light gray
                        borderRadius: "8px", // rounded rectangle
                        margin: "0 4px",
                        minWidth: "36px",
                        height: "36px",
                      },
                  
                      "& .MuiPaginationItem-root:hover": {
                        backgroundColor: "#e5e7eb",
                      },
                  
                      "& .Mui-selected": {
                        backgroundColor: "#000 !important",
                        color: "#fff !important",
                      },
                  
                      "& .Mui-selected:hover": {
                        backgroundColor: "#000",
                      },
                    }}
                      onChange={(e, v) => {
                        okButton(v);
                      }}
                      count={totalPages}
                      page={currentPage}
                    />{" "}
                  </div>}
                  footer={<Footer columns={columns} rows={purchaseList} />}
                />
              ) : (
                <div className="border rounded-sm h-full flex text-sm text-gray-500 items-center justify-center">
                  No Records Found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "none",
        }}
      >
        <div ref={printRef}>
          <GeniusTableForReports
            headerWidth={true}
            data={
              purchaseList[0]
                ? purchaseList.map((p) => {
                    return {
                      ...p,
                      SALE_PRICE: FormatNumber2(p.SALE_PRICE),
                      AMOUNT: FormatNumber2(p.AMOUNT),
                      TAX1_AMT: FormatNumber2(p.TAX1_AMT),
                      TAX2_AMT: FormatNumber2(p.TAX2_AMT),
                      TAX3_AMT: FormatNumber2(p.TAX3_AMT),
                      GTOTAL: FormatNumber2(p.GTOTAL),
                    };
                  })
                : fullItemWiseReport
            }
            fromDate={fromDate}
            ToDate={ToDate}
            columns={printColumns}
            filteredData={summaryList}
          />
        </div>
      </div>
      <CustomerMasterHelp
        status={customerHelpStatus}
        onRowClick={(p) => {
          setCusMobile(p.row.CUSTOMER_MOBILE);
          setCustomerHelpStatus(false);
          setCustomerName(p.row.CUSTOMER_NAME);
        }}
        onClose={() => {
          setCustomerHelpStatus(false);
        }}
      />
      <DrilldownModal
        open={Boolean(drilldown)}
        title={drilldown?.title || ""}
        rows={drilldownRows}
        columns={columns}
        onClose={() => setDrilldown(null)}
      />
    </div>
  );
};

export default IemWiseSaleReport;
